<template>
  <div>
    <a-card :bordered="false" class="header-solid h-full" :bodyStyle="{ padding: '24px' }">
      <template #title>
        <a-row type="flex" align="middle">
          <a-col :span="24" :md="12">
            <h5 class="font-semibold m-0">模板管理</h5>
          </a-col>
          <a-col :span="24" :md="12" style="display: flex; align-items: center; justify-content: flex-end; gap: 12px;">
            <a-select 
              v-model="countryFilter" 
              placeholder="筛选国家" 
              style="width: 150px;"
              allowClear
              @change="onFilterChange"
            >
              <a-select-option value="">全部国家</a-select-option>
              <a-select-option v-for="country in countries" :key="country.code" :value="country.code">
                {{ country.name }}
              </a-select-option>
            </a-select>
            <a-button type="primary" @click="openAddModal">
              新增模板
            </a-button>
          </a-col>
        </a-row>
      </template>
      
      <!-- 模板卡片列表 -->
      <a-row :gutter="[24, 24]">
        <a-col :span="24" :md="8" :lg="6" v-for="template in filteredData" :key="template.id">
          <a-card hoverable class="template-card">
            <div class="template-image" @click="previewImage(template.image)">
              <img :src="template.image" :alt="template.name" />
              <div class="image-overlay">
                <a-icon type="eye" />
              </div>
            </div>
            <div class="template-info">
              <h4 class="template-name">{{ template.name }}</h4>
              <div class="template-meta">
                <a-tag color="blue">{{ template.country_name || template.country }}</a-tag>
              </div>
              <p class="template-url" :title="template.url">
                <a-icon type="link" /> {{ template.url }}
              </p>
            </div>
            <div class="template-actions">
              <a-button type="link" size="small" @click="openEditModal(template)">
                <a-icon type="edit" /> 编辑
              </a-button>
              <a-popconfirm 
                title="确定要删除此模板吗?" 
                ok-text="确定" 
                cancel-text="取消" 
                @confirm="deleteTemplate(template)"
              >
                <a-button type="link" size="small" style="color: red;">
                  <a-icon type="delete" /> 删除
                </a-button>
              </a-popconfirm>
            </div>
          </a-card>
        </a-col>
      </a-row>
      
      <!-- 空状态 -->
      <a-empty v-if="filteredData.length === 0" description="暂无模板" />
    </a-card>

    <!-- 新增/编辑模板弹窗 -->
    <a-modal 
      v-model="modalVisible" 
      :title="isEdit ? '编辑模板' : '新增模板'" 
      centered 
      cancelText="取消" 
      okText="确认" 
      @ok="handleSubmit"
      :confirmLoading="submitLoading"
    >
      <a-form-model :model="form" :rules="rules" ref="formRef">
        <a-form-model-item label="模板名称" prop="name">
          <a-input v-model="form.name" placeholder="请输入模板名称" />
        </a-form-model-item>
        <a-form-model-item label="模板图片" prop="image">
          <a-upload
            name="image"
            list-type="picture-card"
            class="image-uploader"
            :show-upload-list="false"
            :before-upload="beforeUpload"
            :custom-request="handleUpload"
          >
            <img v-if="form.image" :src="form.image" alt="模板图片" style="width: 100%; height: 100%; object-fit: cover;" />
            <div v-else>
              <a-icon :type="uploading ? 'loading' : 'plus'" />
              <div class="ant-upload-text">上传图片</div>
            </div>
          </a-upload>
          <a-input v-model="form.image" placeholder="或直接输入图片URL" style="margin-top: 8px;" />
        </a-form-model-item>
        <a-form-model-item label="下载链接" prop="url">
          <a-input v-model="form.url" placeholder="请输入模板下载链接" />
        </a-form-model-item>
        <a-form-model-item label="国家代码" prop="country">
          <a-input v-model="form.country" placeholder="如: US, JP, KR" />
        </a-form-model-item>
        <a-form-model-item label="国家名称" prop="country_name">
          <a-input v-model="form.country_name" placeholder="如: 美国, 日本, 韩国" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- 图片预览弹窗 -->
    <a-modal 
      v-model="previewVisible" 
      title="图片预览" 
      :footer="null"
      width="600px"
    >
      <img :src="previewImageUrl" alt="预览图片" style="width: 100%;" />
    </a-modal>
  </div>
</template>

<script>
import { templateAdd, templateUpdate, templateDelete, templateUploadImage } from "@/api/template";

export default {
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    countries: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      countryFilter: "",
      modalVisible: false,
      previewVisible: false,
      previewImageUrl: "",
      isEdit: false,
      uploading: false,
      submitLoading: false,
      form: {
        id: "",
        name: "",
        image: "",
        url: "",
        country: "",
        country_name: "",
      },
      rules: {
        name: [{ required: true, message: "请输入模板名称", trigger: "blur" }],
        url: [{ required: true, message: "请输入下载链接", trigger: "blur" }],
        country: [{ required: true, message: "请输入国家代码", trigger: "blur" }],
        country_name: [{ required: true, message: "请输入国家名称", trigger: "blur" }],
      },
    };
  },
  computed: {
    filteredData() {
      if (!this.countryFilter) {
        return this.data;
      }
      return this.data.filter(item => item.country === this.countryFilter);
    },
  },
  methods: {
    onFilterChange() {
      // 筛选条件变化
    },
    openAddModal() {
      this.isEdit = false;
      this.form = {
        id: "",
        name: "",
        image: "",
        url: "",
        country: "",
        country_name: "",
      };
      this.modalVisible = true;
    },
    openEditModal(template) {
      this.isEdit = true;
      this.form = { ...template };
      this.modalVisible = true;
    },
    previewImage(url) {
      this.previewImageUrl = url;
      this.previewVisible = true;
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        this.$message.error('只能上传图片文件!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB!');
        return false;
      }
      return true;
    },
    handleUpload({ file }) {
      this.uploading = true;
      const formData = new FormData();
      formData.append('image', file);
      
      templateUploadImage(formData)
        .then((res) => {
          if (res.status === 200) {
            this.form.image = res.data.url;
            this.$message.success('图片上传成功');
          } else {
            this.$message.error(res.message || '上传失败');
          }
        })
        .catch(() => {
          this.$message.error('上传失败');
        })
        .finally(() => {
          this.uploading = false;
        });
    },
    handleSubmit() {
      this.$refs.formRef.validate((valid) => {
        if (!valid) return;
        
        this.submitLoading = true;
        const apiCall = this.isEdit ? templateUpdate : templateAdd;
        
        apiCall(this.form)
          .then((res) => {
            if (res.status === 200) {
              this.$message.success(this.isEdit ? '模板更新成功' : '模板添加成功');
              this.modalVisible = false;
              this.$emit('refresh');
            } else {
              this.$message.error(res.message || '操作失败');
            }
          })
          .catch(() => {
            this.$message.error('操作失败');
          })
          .finally(() => {
            this.submitLoading = false;
          });
      });
    },
    deleteTemplate(template) {
      templateDelete({ id: template.id })
        .then((res) => {
          if (res.status === 200) {
            this.$message.success('模板删除成功');
            this.$emit('refresh');
          } else {
            this.$message.error(res.message || '删除失败');
          }
        })
        .catch(() => {
          this.$message.error('删除失败');
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.template-card {
  border-radius: 8px;
  overflow: hidden;
  
  .template-image {
    position: relative;
    height: 200px;
    overflow: hidden;
    cursor: pointer;
    background: #f5f5f5;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s;
    }
    
    .image-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s;
      
      .anticon {
        font-size: 32px;
        color: #fff;
      }
    }
    
    &:hover {
      img {
        transform: scale(1.05);
      }
      
      .image-overlay {
        opacity: 1;
      }
    }
  }
  
  .template-info {
    padding: 12px 0;
    
    .template-name {
      margin: 0 0 8px;
      font-size: 16px;
      font-weight: 600;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .template-meta {
      margin-bottom: 8px;
    }
    
    .template-url {
      margin: 0;
      font-size: 12px;
      color: #999;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      
      .anticon {
        margin-right: 4px;
      }
    }
  }
  
  .template-actions {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #f0f0f0;
    padding-top: 12px;
  }
}

.image-uploader {
  ::v-deep .ant-upload {
    width: 128px;
    height: 128px;
  }
}
</style>
